import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HeaderTwo from '../common/header/HeaderTwo';
import { MapView as MapComponent, ListingCard, FilterSidebar } from '../components/directory';
import { listings } from '../data';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';

/**
 * MapView - Interactive map page for browsing local food directory
 * Full-screen interactive map with listing markers
 */
const MapViewPage = () => {
  const [selectedListing, setSelectedListing] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    categories: [],
    products: [],
    distance: 'any',
    features: [],
  });

  // Filter listings
  const filteredListings = listings.filter(listing => {
    if (filters.categories.length > 0 && !filters.categories.includes(listing.type)) {
      return false;
    }
    if (filters.products.length > 0) {
      const hasProduct = listing.products.some(product =>
        filters.products.some(fp => product.toLowerCase().includes(fp.toLowerCase()))
      );
      if (!hasProduct) return false;
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        listing.name.toLowerCase().includes(searchLower) ||
        listing.description.toLowerCase().includes(searchLower) ||
        listing.city.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    return true;
  });

  const handleMarkerClick = (listing) => {
    setSelectedListing(listing);
    setShowSidebar(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      categories: [],
      products: [],
      distance: 'any',
      features: [],
    });
  };

  return (
    <div style={styles.pageWrapper}>
      <HeaderTwo />

      <div style={styles.mapPageContainer}>
        {/* Sidebar Toggle */}
        <button
          style={{
            ...styles.sidebarToggle,
            left: showSidebar ? '380px' : '0',
          }}
          onClick={() => setShowSidebar(!showSidebar)}
        >
          {showSidebar ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>

        {/* Left Sidebar */}
        <div
          style={{
            ...styles.sidebar,
            transform: showSidebar ? 'translateX(0)' : 'translateX(-100%)',
          }}
        >
          {/* Sidebar Header */}
          <div style={styles.sidebarHeader}>
            <div>
              <h2 style={styles.sidebarTitle}>Explore Map</h2>
              <p style={styles.sidebarSubtitle}>
                {filteredListings.length} listings in view
              </p>
            </div>
            <button
              style={styles.filterToggleBtn}
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal size={18} />
              {showFilters ? 'Hide' : 'Filters'}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div style={styles.filtersPanel}>
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                compact={true}
              />
            </div>
          )}

          {/* Listings List */}
          <div style={styles.listingsScroll}>
            {selectedListing ? (
              <div style={styles.selectedListingWrap}>
                <button
                  style={styles.backToListBtn}
                  onClick={() => setSelectedListing(null)}
                >
                  <ChevronLeft size={18} />
                  Back to list
                </button>
                <ListingCard listing={selectedListing} variant="list" />
                <Link
                  to={`/listing/${selectedListing.slug}`}
                  style={styles.viewDetailsBtn}
                >
                  View Full Details
                </Link>
              </div>
            ) : (
              <div style={styles.listingsList}>
                {filteredListings.map(listing => (
                  <div
                    key={listing.id}
                    style={styles.listingItem}
                    onClick={() => setSelectedListing(listing)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--thm-bg-sage)';
                      e.currentTarget.style.borderColor = 'var(--thm-base)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <div style={styles.listingItemImage}>
                      <img
                        src={listing.images?.[0] || '/images/placeholder-farm.jpg'}
                        alt={listing.name}
                        style={styles.listingItemImg}
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&h=150&fit=crop';
                        }}
                      />
                      <span style={styles.listingItemType}>{listing.type}</span>
                    </div>
                    <div style={styles.listingItemContent}>
                      <h4 style={styles.listingItemName}>{listing.name}</h4>
                      <p style={styles.listingItemCity}>{listing.city}, {listing.state}</p>
                      <div style={styles.listingItemRating}>
                        <span style={styles.ratingStars}>★ {listing.rating}</span>
                        <span style={styles.reviewCount}>({listing.reviewCount})</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Map Container */}
        <div style={styles.mapContainer}>
          <MapComponent
            listings={filteredListings}
            onMarkerClick={handleMarkerClick}
            selectedListing={selectedListing}
            fullScreen={true}
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageWrapper: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  mapPageContainer: {
    flex: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  sidebar: {
    width: '380px',
    backgroundColor: '#fff',
    borderRight: '1px solid rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    transition: 'transform 0.3s ease',
  },
  sidebarToggle: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '32px',
    height: '64px',
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.1)',
    borderLeft: 'none',
    borderRadius: '0 8px 8px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 101,
    transition: 'left 0.3s ease',
    boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
  },
  sidebarHeader: {
    padding: '20px',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    margin: 0,
  },
  sidebarSubtitle: {
    fontSize: '14px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    margin: '4px 0 0',
  },
  filterToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: 'var(--thm-bg-sage)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
    color: 'var(--thm-base)',
    cursor: 'pointer',
    fontWeight: '600',
  },
  filtersPanel: {
    padding: '16px 20px',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  listingsScroll: {
    flex: 1,
    overflowY: 'auto',
  },
  selectedListingWrap: {
    padding: '20px',
  },
  backToListBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--thm-base)',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '16px',
    padding: 0,
  },
  viewDetailsBtn: {
    display: 'block',
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    textAlign: 'center',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
    marginTop: '16px',
  },
  listingsList: {
    padding: '12px',
  },
  listingItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#fff',
    borderRadius: '10px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid transparent',
  },
  listingItemImage: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0,
  },
  listingItemImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  listingItemType: {
    position: 'absolute',
    bottom: '4px',
    left: '4px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  listingItemContent: {
    flex: 1,
    minWidth: 0,
  },
  listingItemName: {
    fontSize: '15px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    margin: '0 0 4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listingItemCity: {
    fontSize: '13px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    margin: '0 0 6px',
  },
  listingItemRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  ratingStars: {
    color: 'var(--thm-accent)',
    fontSize: '13px',
    fontWeight: '600',
  },
  reviewCount: {
    color: 'var(--thm-gray)',
    fontSize: '12px',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
};

export default MapViewPage;
